import React from "react";
import Select from "react-select";
import { names } from "../../../helpers/StringHelper";

const BRAND_PURPLE = "#6e4be4";
const BORDER_GREY = "#d1d1d1";
const ERROR_RED = "red";
const MENU_Z_INDEX = 9999;

const buildSelectStyles = (hasError, border) => ({
  control: (base, state) => ({
    ...base,
    backgroundColor: "#fcfcfd",
    border: "none",
    borderRadius: 0,
    borderBottom: `solid ${border || 2}px ${
      hasError ? ERROR_RED : state.isFocused ? BRAND_PURPLE : BORDER_GREY
    }`,
    boxShadow: "none",
    minHeight: 38,
    "&:hover": {
      borderBottom: `solid ${border || 2}px ${
        hasError ? ERROR_RED : state.isFocused ? BRAND_PURPLE : BORDER_GREY
      }`,
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "5px 10px",
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 400,
    color: "#495057",
  }),
  singleValue: (base) => ({
    ...base,
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 400,
    color: "#495057",
  }),
  input: (base) => ({
    ...base,
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 400,
    color: "#495057",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "#9a9a9a" }),
  clearIndicator: (base) => ({ ...base, color: "#9a9a9a" }),
  placeholder: (base) => ({
    ...base,
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 400,
    color: "#9a9a9a",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor:
      state.isSelected || state.isFocused ? BRAND_PURPLE : base.backgroundColor,
    color: state.isSelected || state.isFocused ? "#fff" : base.color,
    "&:active": {
      backgroundColor: BRAND_PURPLE,
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: MENU_Z_INDEX }),
  menu: (base) => ({ ...base, zIndex: MENU_Z_INDEX }),
  menuList: (base) => ({ ...base, overscrollBehavior: "contain" }),
});

// Canonical react-select field for the app: underline control matching the
// legacy TextField look, brand-purple hover/focus/selected state, and a
// portalled menu so dropdowns never get clipped by a lower z-index.
//
// The portal is repositioned on every scroll (page or a nested scrollable
// container) instead of closing the menu: react-select recomputes the
// portalled menu's coordinates from the control's live bounding rect on
// every render, so forcing a re-render keeps the menu anchored to the field.
//
// The wrapping form-group/label/error markup only renders when `label`
// and/or `errors` are passed — pass `noFormGroup` to always render just the
// bare <Select/> (e.g. when the call site already renders its own label).
class ReactSelect extends React.Component {
  trackingScroll = false;
  scrollFrame = null;

  componentWillUnmount() {
    this.stopTrackingScroll();
  }

  startTrackingScroll = () => {
    if (this.trackingScroll) return;
    this.trackingScroll = true;
    document.addEventListener("scroll", this.handleScroll, true);
  };

  stopTrackingScroll = () => {
    if (!this.trackingScroll) return;
    this.trackingScroll = false;
    document.removeEventListener("scroll", this.handleScroll, true);
    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  };

  handleScroll = () => {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.forceUpdate();
    });
  };

  onMenuOpen = (...args) => {
    this.startTrackingScroll();
    if (this.props.onMenuOpen) this.props.onMenuOpen(...args);
  };

  onMenuClose = (...args) => {
    this.stopTrackingScroll();
    if (this.props.onMenuClose) this.props.onMenuClose(...args);
  };

  render() {
    let {
      label,
      errors,
      invalid,
      required,
      processing,
      className,
      style,
      noFormGroup,
      border,
      onMenuOpen,
      onMenuClose,
      ...selectProps
    } = this.props;

    let errorList = errors && (Array.isArray(errors) ? errors : [errors]);
    let hasError =
      typeof invalid === `boolean`
        ? invalid
        : Boolean(errorList && errorList.length);

    let field = (
      <>
        {label ? <label>{label}</label> : null}
        <Select
          classNamePrefix="react-select"
          styles={buildSelectStyles(hasError, border)}
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : null
          }
          {...selectProps}
          onMenuOpen={this.onMenuOpen}
          onMenuClose={this.onMenuClose}
        />
        {errorList &&
          errorList.map((error, index) => (
            <p className="error-message" key={index}>
              {typeof error === "string" ? error : error?.message}
            </p>
          ))}
      </>
    );

    if (noFormGroup) return field;

    return (
      <div
        className={names(
          `form-group`,
          `animated fadeIn faster`,
          hasError && `has-error`,
          required && `required`,
          processing && `processing`,
          className,
        )}
        style={style}
      >
        {field}
      </div>
    );
  }
}

export default ReactSelect;
